import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, Divider, Button, Stack, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import { Referral, ReferralStage, ReferralSource, ReferralStatus, FlagSeverity, DecisionOutcome } from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { useTransitionAttention } from '~/providers/TransitionAttentionProvider';
import SourceBadge from '../common/SourceBadge';

const STAGES = [ReferralStage.PROCESSING, ReferralStage.DECISION, ReferralStage.EXECUTION] as const;

const STAGE_LABELS: Record<ReferralStage, string> = {
  [ReferralStage.INTAKE]: 'Intake',
  [ReferralStage.PROCESSING]: 'Processing',
  [ReferralStage.DECISION]: 'Decision',
  [ReferralStage.EXECUTION]: 'Execution',
};

const STATUS_LABELS: Record<ReferralStatus, string> = {
  [ReferralStatus.RECEIVED]: 'Received',
  [ReferralStatus.PROCESSING]: 'Processing',
  [ReferralStatus.PROCESSING_COMPLETE]: 'Processing complete',
  [ReferralStatus.AWAITING_INFO]: 'Awaiting info',
  [ReferralStatus.RECOMMENDATION_READY]: 'Ready for decision',
  [ReferralStatus.PENDING_REVIEW]: 'Pending review',
  [ReferralStatus.ACCEPTED]: 'Accepted',
  [ReferralStatus.DECLINED]: 'Declined',
  [ReferralStatus.AUTO_DECLINED]: 'Auto-declined',
  [ReferralStatus.BLOCKED]: 'Blocked',
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── List item ────────────────────────────────────────────────────────────────

const ListItem: React.FC<{
  referral: Referral;
  selected: boolean;
  onClick: () => void;
}> = ({ referral, selected, onClick }) => {
  const { isUnseen } = useTransitionAttention();
  const unseen = isUnseen(referral.id);
  const hasBlocker = referral.flags.some((f) => f.severity === FlagSeverity.BLOCKING);
  const hasWarning = referral.flags.some((f) => f.severity === FlagSeverity.WARNING);

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: selected
          ? 'primary.main'
          : hasBlocker
          ? 'error.main'
          : hasWarning
          ? 'warning.main'
          : 'transparent',
        bgcolor: selected ? 'primary.50' : 'transparent',
        '&:hover': { bgcolor: selected ? 'primary.50' : 'action.hover' },
        transition: 'background-color 0.1s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        {unseen && (
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
        )}
        <Typography
          variant="body2"
          fontWeight={unseen ? 700 : selected ? 600 : 500}
          noWrap
          sx={{ flex: 1 }}
        >
          {referral.patient.name}
        </Typography>
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary" noWrap>
          {STATUS_LABELS[referral.status]}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {timeAgo(referral.receivedAt)}
        </Typography>
      </Stack>
    </Box>
  );
};

// ── Detail panel ─────────────────────────────────────────────────────────────

const SECTION_LABEL = {
  textTransform: 'uppercase' as const,
  fontWeight: 600,
  letterSpacing: 0.5,
  fontSize: '0.68rem',
  color: 'grey.500',
  mb: 0.25,
};

const DetailField: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={SECTION_LABEL}>{label}</Typography>
    <Typography variant="body2">{value ?? '—'}</Typography>
  </Box>
);

const DetailPanel: React.FC<{ referral: Referral }> = ({ referral }) => {
  const navigate = useNavigate();
  const { patient } = referral;
  const blocking = referral.flags.filter((f) => f.severity === FlagSeverity.BLOCKING);
  const warning = referral.flags.filter((f) => f.severity === FlagSeverity.WARNING);
  const hasBlocker = blocking.length > 0;
  const hasWarning = warning.length > 0;

  const recColor =
    referral.aiRecommendation === DecisionOutcome.ACCEPT ? 'success.main' :
    referral.aiRecommendation === DecisionOutcome.DECLINE ? 'error.main' : 'text.secondary';

  const recLabel =
    referral.aiRecommendation === DecisionOutcome.ACCEPT ? 'Accept' :
    referral.aiRecommendation === DecisionOutcome.DECLINE ? 'Decline' :
    referral.aiRecommendation === DecisionOutcome.PENDING ? 'Pending' : 'Pending';

  return (
    <Box sx={{ p: 3, overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{patient.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              <Chip
                label={STAGE_LABELS[referral.stage]}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
              />
              <Chip
                label={STATUS_LABELS[referral.status]}
                size="small"
                sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
              />
              <SourceBadge source={referral.source} size="small" />
              {(referral.priorEncounters?.length ?? 0) > 0 ? (
                <Chip
                  icon={<HistoryIcon sx={{ fontSize: '0.7rem !important' }} />}
                  label="Returning"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 24, fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                />
              ) : (
                <Chip
                  label="New patient"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', height: 24, color: 'text.disabled', borderColor: 'divider' }}
                />
              )}
            </Stack>
          </Box>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: '0.8rem' }} />}
            onClick={() => navigate(`/transition/referral/${referral.id}`)}
            sx={{ flexShrink: 0, ml: 2, fontSize: '0.75rem' }}
          >
            Full record
          </Button>
        </Stack>
        <Typography variant="caption" color="text.disabled">
          Received {timeAgo(referral.receivedAt)}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {/* Patient & Clinical */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
        <DetailField label="Date of Birth" value={new Date(patient.dob + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
        <DetailField label="Program" value={patient.requestedProgram} />
        <DetailField label="Insurance" value={patient.insurance} />
        <DetailField label="Member ID" value={patient.insuranceMemberId ?? '—'} />
        <Box sx={{ gridColumn: '1 / -1' }}>
          <DetailField
            label="Diagnosis"
            value={patient.diagnosisLabels.map((l, i) => `${patient.diagnosisCodes[i]} · ${l}`).join(', ')}
          />
        </Box>
        <DetailField label="Referring Provider" value={patient.referringProvider} />
        <DetailField label="Facility" value={patient.referringFacility} />
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {/* Flags */}
      {referral.flags.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" sx={{ ...SECTION_LABEL, mb: 1, display: 'block' }}>Flags</Typography>
          <Stack spacing={0.75}>
            {referral.flags.map((flag) => (
              <Box
                key={flag.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                {flag.severity === FlagSeverity.BLOCKING ? (
                  <ErrorIcon sx={{ fontSize: '0.9rem', color: 'error.main', mt: 0.15 }} />
                ) : flag.severity === FlagSeverity.WARNING ? (
                  <WarningAmberIcon sx={{ fontSize: '0.9rem', color: 'warning.main', mt: 0.15 }} />
                ) : (
                  <Box sx={{ width: '0.9rem' }} />
                )}
                <Typography variant="caption" color="text.secondary">{flag.label}</Typography>
              </Box>
            ))}
          </Stack>
          <Divider sx={{ mt: 2.5, mb: 2.5 }} />
        </Box>
      )}

      {/* AI Recommendation */}
      {referral.aiRecommendation !== DecisionOutcome.PENDING && (
        <Box>
          <Typography variant="caption" sx={{ ...SECTION_LABEL, display: 'block', mb: 1 }}>
            AI Recommendation
          </Typography>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
            <CheckCircleIcon sx={{ fontSize: '0.9rem', color: recColor }} />
            <Typography variant="body2" fontWeight={600} sx={{ color: recColor }}>
              {recLabel}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {referral.aiRecommendationReason}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  filterSource: ReferralSource | 'ALL';
  filterStage: ReferralStage | 'ALL';
}

const PipelineMasterDetail: React.FC<Props> = ({ filterSource, filterStage }) => {
  const { referrals } = useTransitionContext();
  const { isUnseen } = useTransitionAttention();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const result = referrals.filter((r) => {
      if (r.status === ReferralStatus.DECLINED || r.status === ReferralStatus.AUTO_DECLINED) return false;
      if (filterSource !== 'ALL' && r.source !== filterSource) return false;
      if (filterStage !== 'ALL' && r.stage !== filterStage) return false;
      return true;
    });
    return [...result].sort((a, b) => {
      const au = isUnseen(a.id) ? 0 : 1;
      const bu = isUnseen(b.id) ? 0 : 1;
      if (au !== bu) return au - bu;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [referrals, filterSource, filterStage, isUnseen]);

  const selected = filtered.find((r) => r.id === selectedId) ?? filtered[0] ?? null;
  const visibleStages = filterStage === 'ALL' ? STAGES : [filterStage as ReferralStage];

  return (
    <Box sx={{ flex: 1, display: 'flex', minHeight: 0, gap: 0 }}>
      {/* Left: list */}
      <Paper
        variant="outlined"
        sx={{
          flex: '0 0 280px',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          borderRadius: 1,
          mr: 2,
        }}
      >
        {visibleStages.map((stage) => {
          const rows = filtered.filter((r) => r.stage === stage);
          return (
            <Box key={stage}>
              <Box
                sx={{
                  px: 2,
                  py: 0.75,
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Typography variant="overline" fontWeight={700} sx={{ letterSpacing: '0.08em', lineHeight: 1, fontSize: '0.65rem' }}>
                  {STAGE_LABELS[stage]}
                </Typography>
                <Chip label={rows.length} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
              </Box>
              {rows.length === 0 ? (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 2, py: 1.5 }}>
                  No referrals
                </Typography>
              ) : (
                rows.map((r) => (
                  <ListItem
                    key={r.id}
                    referral={r}
                    selected={r.id === (selected?.id ?? null)}
                    onClick={() => setSelectedId(r.id)}
                  />
                ))
              )}
            </Box>
          );
        })}
      </Paper>

      {/* Right: detail */}
      <Paper variant="outlined" sx={{ flex: 1, overflowY: 'auto', borderRadius: 1 }}>
        {selected ? (
          <DetailPanel referral={selected} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.disabled">
              Select a referral to preview
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PipelineMasterDetail;
